import React, { useState, useEffect, useContext } from 'react';
import { Button, Input, Table, Typography, Modal, Spin, Popover } from 'antd';
import { SearchOutlined, DeleteOutlined, ExclamationCircleOutlined, PauseOutlined, PlayCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { fetchKeywords, deleteKeyword, updateKeywordStatus } from '../../api/Api';
import { useSelector } from 'react-redux';
import { useFunction } from '../../../../hooks/useFeatureGating';
import { FUNCTIONS } from '../../../../constant/featureSlugs';
import PremiumUpgradeScreen from '../../../../components/commonComponents/PremiumUpgradeScreen/PremiumUpgradeScreen';
// notification
import NotificationContext from '../../../../providers/NotificationProvider';
import { showErrorToast, showSuccessToast } from '../../../../components/commonComponents/gxAntdToast/toastUtils';

const { Text } = Typography;
const { Search } = Input;
const { confirm } = Modal;

const Keywords = () => {
  const [searchText, setSearchText] = useState('');
  const currentBrand = useSelector((state) => state.app.currentBrand);
  const { isEnabled: canUseKeywords } = useFunction(FUNCTIONS.SEQUENCE_KEYWORD_TRIGGER);
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [popoverVisible, setPopoverVisible] = useState({});

  const notification = useContext(NotificationContext);
  // Fetch data with pagination and search
  const fetchData = async ({ current = 1, pageSize = 10, search } = {}) => {
    if (!currentBrand?.brand_id) return;
    setLoading(true);
    try {
      const response = await fetchKeywords(currentBrand.brand_id, {
        page: current,
        limit: pageSize,
        search,
      });

      const { keywords, total_count } = response.data;

      setDataSource(
        (keywords || []).map((item) => ({
          ...item,
          key: item.id.toString(),
          matching_method: item.match_type === 'exact' ? 'Exact Match' : 'Fuzzy Match',
        }))
      );

      setPagination((prev) => ({
        ...prev,
        current,
        pageSize,
        total: total_count,
      }));
    } catch (error) {
      console.error('Error fetching keywords:', error);
      setDataSource([]);
      setPagination({
        current: 1,
        pageSize: 10,
        total: 0,
      });
      showErrorToast(notification, 'Failed to fetch keywords');
    } finally {
      setLoading(false);
    }
  };

  // Initial load and reload on brand change
  useEffect(() => {
    if (currentBrand?.brand_id) {
      fetchData({ current: 1, pageSize: pagination.pageSize, search: searchText });
    }
    // eslint-disable-next-line
  }, [currentBrand?.brand_id]);

  // Table column definitions with enhanced styling
  const columns = [
    {
      title: 'KEYWORD NAME',
      dataIndex: 'actual_keyword',
      key: 'keyword',
      render: (text) => (
        <span className="font-[500] text-gray-900">{text}</span>
      ),
    },
    {
      title: 'SEQUENCE NAME',
      dataIndex: 'campaign_sequence_name',
      key: 'sequence',
      render: (text) => text || '—',
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          'ACTIVE': { 
            bg: 'bg-green-50', 
            text: 'text-green-700', 
            dot: 'bg-green-500',
            border: 'border-green-100'
          },
          'PAUSED': { 
            bg: 'bg-yellow-50', 
            text: 'text-yellow-700', 
            dot: 'bg-yellow-500',
            border: 'border-yellow-100'
          },
          'DRAFT': { 
            bg: 'bg-gray-50', 
            text: 'text-gray-700', 
            dot: 'bg-gray-400',
            border: 'border-gray-100'
          }
        };
        
        const config = statusConfig[status] || statusConfig['DRAFT'];
        const formattedStatus = status.charAt(0) + status.slice(1).toLowerCase();
        
        return (
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text} ${config.border} border`}>
            <span className={`w-2 h-2 rounded-full mr-2 ${config.dot}`}></span>
            {formattedStatus}
          </div>
        );
      },
    },
    {
      title: 'MATCH TYPE',
      dataIndex: 'match_type',
      key: 'match_type',
      align: 'center',
      render: (matchType, record) => (
        <div className="flex flex-col items-center">
          <span className="text-gray-900 text-sm">
            {matchType === 'exact' ? 'Exact Match' : 'Fuzzy Match'}
          </span>
          {matchType !== 'exact' && record.match_threshold && (
            <span className="text-xs text-gray-500 mt-0.5">
              {record.match_threshold}% match
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'TRIGGERED',
      dataIndex: 'triggered_count',
      key: 'triggered_count',
      align: 'center',
      render: (count) => (
        <div className="flex items-center justify-center">
          <span className="text-gray-900">{count || 0}</span>
        </div>
      ),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      fixed: 'right',
      align: 'center',
      width: 50,
      render: (_, record) => (
        <div className="flex justify-center items-center">
          <Popover
            placement="bottomRight"
            trigger="click"
            open={popoverVisible[record.key] || false}
            onOpenChange={(visible) => togglePopover(record.key, visible)}
            overlayClassName="action-popover"
            content={
              <div className="flex flex-col py-1 min-w-[120px]">
                {getMenuItems(record).map((item) => (
                  <button
                    key={item.key}
                    onClick={item.onClick}
                    disabled={item.disabled}
                    className={`flex items-center gap-2 px-4 py-2 text-sm ${item.danger ? 'text-red-500 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <span className="text-base">{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            }
          >
            <div 
              className="p-1 hover:bg-gray-100 rounded-md cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                togglePopover(record.key, true);
              }}
            >
              <MoreOutlined className="text-lg text-gray-400 hover:text-gray-600" />
            </div>
          </Popover>
        </div>
      ),
    },
  ];

  // Table page/size/sort/search handler
  const handleTableChange = (newPagination, filters, sorter) => {
    fetchData({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      search: searchText,
    });
  };

  const togglePopover = (id, visible) => {
    setPopoverVisible(prev => ({
      ...prev,
      [id]: visible
    }));
  };

  const handleStatusToggle = async (record) => {
    const newStatus = record.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    
    if (newStatus === 'PAUSED') {
      confirm({
        title: 'Pause Keyword',
        icon: <ExclamationCircleOutlined className="text-yellow-500" />,
        content: 'Pausing this keyword will stop the sequence from triggering for this keyword. Are you sure you want to continue?',
        okText: 'Pause',
        okButtonProps: { 
          className: '!bg-yellow-500 hover:!bg-yellow-600 border-0',
        },
        cancelText: 'Cancel',
        cancelButtonProps: {
          className: '!text-gray-700 !border-gray-200',
        },
        centered: true,
        maskClosable: true,
        onOk: () => updateKeywordStatusAndRefresh(record, newStatus),
        onCancel: () => {
          setPopoverVisible(prev => ({
            ...prev,
            [record.key]: false
          }));
        },
      });
    } else {
      await updateKeywordStatusAndRefresh(record, newStatus);
    }
  };

  const updateKeywordStatusAndRefresh = async (record, newStatus) => {
    try {
      await updateKeywordStatus(currentBrand.brand_id, {
        id: record.key,
        status: newStatus.toLowerCase()
      });
      showSuccessToast(notification, `Keyword ${newStatus === 'ACTIVE' ? 'activated' : 'paused'} successfully`);
      setPopoverVisible(prev => ({
        ...prev,
        [record.key]: false
      }));
      fetchData({
        current: pagination.current,
        pageSize: pagination.pageSize,
        search: searchText,
      });
    } catch (error) {
      showErrorToast(notification, error.response?.data?.error || `Failed to ${newStatus === 'ACTIVE' ? 'activate' : 'pause'} keyword`);
      console.error(`Error ${newStatus === 'ACTIVE' ? 'activating' : 'pausing'} keyword:`, error);
    }
  };

  const handleDelete = (key) => {
    confirm({
      title: 'Delete Keyword',
      icon: <ExclamationCircleOutlined className="text-red-500" />,
      content: 'This action cannot be undone. The keyword will be permanently removed.',
      okText: 'Delete',
      okButtonProps: { 
        danger: true,
        className: '!bg-red-600 hover:!bg-red-700 border-0',
      },
      cancelText: 'Cancel',
      centered: true,
      maskClosable: true,
      onOk: async () => {
        try {
          await deleteKeyword(currentBrand.brand_id, { id: key });
          showSuccessToast(notification, 'Keyword deleted successfully');
          setPopoverVisible(prev => ({
            ...prev,
            [key]: false
          }));
          fetchData({
            current: pagination.current,
            pageSize: pagination.pageSize,
            search: searchText,
          });
        } catch (error) {
          showErrorToast(notification, error.response?.data?.error || 'Failed to delete keyword');
          console.error('Error deleting keyword:', error);
        }
      },
    });
  };

  const getMenuItems = (record) => {
    const items = [];
    
    // Only show Pause/Activate for non-Draft status
    if (record.status !== 'Draft') {
      items.push({
        key: 'status',
        label: record.status === 'ACTIVE' ? 'Pause' : 'Activate',
        icon: record.status === 'ACTIVE' ? <PauseOutlined /> : <PlayCircleOutlined />,
        onClick: (e) => {
          if (e && e.stopPropagation) e.stopPropagation();
          handleStatusToggle(record);
        }
      });
    }
    
    // Always show Delete
    items.push({
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        handleDelete(record.key);
      },
    });
    
    return items;
  };

  // Search handler
  const handleSearch = (value) => {
    setSearchText(value);
    fetchData({
      current: 1,
      pageSize: pagination.pageSize,
      search: value,
    });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {!canUseKeywords ? (
          <div className="bg-white rounded-xl">
            <PremiumUpgradeScreen 
              title="Unlock Keyword Triggers"
              message="Automatically trigger sequences based on incoming user messages. Boost your engagement with keyword-based automation. Upgrade to access."
            />
          </div>
        ) : (
          <>
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-[600] text-gray-900">Keywords</h1>
                <Text type="secondary" className="text-sm">
                  Manage your automated response keywords
                </Text>
              </div>
            </div>

            {/* Search and Filter Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <Search
                    placeholder="Search keywords"
                    allowClear
                    enterButton={
                      <Button 
                        type="primary" 
                        className="h-10 px-4 bg-blue-600 hover:bg-blue-700 border-0 rounded-r-lg"
                      >
                        <SearchOutlined />
                      </Button>
                    }
                    size="large"
                    onSearch={handleSearch}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    className="w-full md:max-w-md"
                  />
                </div>
              </div>
              
              {/* Table Section */}
              <div className="border border-gray-100 rounded-lg overflow-hidden">
                <Spin spinning={loading}>
                  <Table 
                    columns={columns} 
                    dataSource={dataSource}
                    rowKey="key"
                    pagination={{
                      ...pagination,
                      showSizeChanger: true,
                      showTotal: (total) => (
                        <span className="text-sm text-gray-600">
                          Showing <span className="font-[500]">{dataSource.length}</span> of <span className="font-[500]">{total}</span> keywords
                        </span>
                      ),
                      className: 'px-4 py-2',
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 'max-content' }}
                    className="[&_.ant-table-thead>tr>th]:bg-gray-50 [&_.ant-table-thead>tr>th]:text-gray-600 [&_.ant-table-thead>tr>th]:font-[500] [&_.ant-table-thead>tr>th]:text-xs [&_.ant-table-thead>tr>th]:uppercase [&_.ant-table-thead>tr>th]:tracking-wider"
                    rowClassName={() => 'hover:bg-gray-50 transition-colors duration-150'}
                    locale={{
                      emptyText: (
                        <div className="py-12 flex flex-col items-center justify-center text-gray-400">
                          <SearchOutlined className="text-3xl mb-2" />
                          <p className="text-base">No keywords found</p>
                          <p className="text-sm">Add a new keyword to get started</p>
                        </div>
                      )
                    }}
                  />
                </Spin>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Keywords;
